import React from "react";
import { motion } from "framer-motion";

const PURPLE_GRADIENT = "linear-gradient(0.68deg, #A28FDC 0.74%, #7C3AED 74.61%)";

const EvDailyCardMob = ({ car, onBook }) => {
  const price = car.rentPerHr != null ? Number(car.rentPerHr) : null;

  return (
    <motion.article
      layout
      className={`relative bg-white border-[0.6px] border-[#E5E7EB] rounded-[9px] shadow-[0px_6px_19.4px_-8.25px_rgba(212,184,184,0.07)] flex items-stretch justify-between gap-2 p-3 overflow-hidden min-w-0 ${car.isSoldOut ? "opacity-60" : ""}`}
    >
      {/* Left: details */}
      <div className="flex flex-col justify-between gap-2 w-[165px] shrink-0">
        {/* Top section */}
        <div className="flex flex-col gap-2">
          {/* Name */}
          <span className="text-[#252525] text-[16px] font-semibold font-['Outfit'] leading-[1.2] tracking-[0.3px] text-left block">
            {car.name}
          </span>

          {/* Badges + Specs */}
          <div className="flex flex-col gap-[5.5px]">
            {/* Row 1: Electric + Type badge */}
            <div className="flex items-center gap-[5.5px]">
              <div className="bg-[rgba(88,255,88,0.36)] flex gap-[4px] h-[18px] items-center px-[6px] rounded-[4.5px] shrink-0">
                <img alt="" src="/assets/icons/ev/leaf.svg" className="w-[10px] h-[10px] shrink-0" />
                <span className="text-[#005a00] text-[10px] font-medium font-['Outfit'] leading-none whitespace-nowrap">Electric</span>
              </div>
              {car.bodyTypeText && (
                <span className="px-[6px] py-[1.5px] bg-[#F9FAFB] border-[0.8px] border-[#F3F3F9] rounded-[3.2px] text-[#1D4ED8] text-[10px] font-semibold font-['Outfit'] uppercase tracking-[0.75px] whitespace-nowrap truncate min-w-0">
                  {car.bodyTypeText}
                </span>
              )}
            </div>

            {/* Row 2: Seats + Transmission */}
            <div className="flex gap-[5.5px]">
              <div className="bg-[#f7f9fd] border-[0.7px] border-[#edf0f7] flex flex-1 gap-[6px] items-center px-[6px] py-[4px] rounded-[5.5px] min-w-0">
                <img alt="" src="/assets/icons/ev/users-round.svg" className="w-[12px] h-[12px] shrink-0" />
                <span className="text-[#161e2c] text-[10px] font-medium font-['Outfit'] leading-[12px] truncate">
                  {car.seater} Seats
                </span>
              </div>
              <div className="bg-[#f7f9fd] border-[0.7px] border-[#edf0f7] flex flex-1 gap-[6px] items-center px-[6px] py-[4px] rounded-[5.5px] min-w-0">
                <img alt="" src="/assets/icons/ev/transmission.svg" className="w-[12px] h-[12px] shrink-0" />
                <span className="text-[#161e2c] text-[10px] font-medium font-['Outfit'] leading-[12px] truncate">
                  {car.transmissionTypeText}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: Pricing */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-[3px]">
            {price != null ? (
              <div className="flex items-end leading-none text-[#cd2525] whitespace-nowrap">
                <span className="text-[20px] font-bold font-['Outfit'] tracking-[-0.2px]">₹{price.toLocaleString("en-IN")}/</span>
                <span className="text-[14px] font-medium font-['Outfit'] tracking-[0.42px]">hr</span>
              </div>
            ) : (
              <span className="text-[#9CA3AF] text-[14px] font-medium font-['Outfit'] leading-none">Price unavailable</span>
            )}
            <span className="text-black text-[10px] font-bold font-['Outfit'] leading-[15px] tracking-[0.45px] text-left block">
              {car.baseFare != null && car.taxAmount != null && (
                <span>₹{(Number(car.baseFare) + Number(car.taxAmount)).toLocaleString("en-IN")} · </span>
              )}
              Incl. Taxes &amp; Excl. Fees
            </span>
          </div>

          {car.extraKmRental != null && car.totalFreeKms != null && (
            <p className="text-[#727272] text-[10px] font-medium font-['Outfit'] tracking-[0.1px] leading-none text-left">
              ₹{car.extraKmRental}/km after{" "}
              <span className="text-[#1d4ed8] font-bold">{car.totalFreeKms} km</span>
              {" "}limit
            </p>
          )}
        </div>
      </div>

      {/* Right: image + driving range + Book Now */}
      <div className="flex flex-col items-stretch justify-between gap-[6px] flex-1 min-w-0 max-w-[184px]">
        <div className="w-full h-[88px]">
          <img
            src={car.imageUrl || "/assets/sample.png"}
            alt={car.name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        </div>

        <div className="flex flex-col gap-[6px] w-full">
          {car.drivingRange && (
            <div className="flex gap-[6px] items-center justify-center">
              <img alt="" src="/assets/icons/ev/route.svg" className="w-[14px] h-[14px] shrink-0" />
              <div className="flex flex-col text-left">
                <span className="text-[#161e2c] text-[10px] font-medium font-['Outfit'] leading-[12px]">
                  {car.drivingRange} km
                </span>
                <span className="text-[#7e8ba5] text-[8px] font-normal font-['Outfit'] leading-[10px] whitespace-nowrap">
                  Driving Range (per charge)
                </span>
              </div>
            </div>
          )}
          <button
            disabled={car.isSoldOut}
            onClick={() => onBook(car)}
            className={`w-full h-[28.4px] rounded-[6px] text-[12px] font-medium font-['Outfit'] tracking-[0.32px] text-white border-[0.6px] whitespace-nowrap ${car.isSoldOut ? "bg-gray-200 text-gray-400 border-gray-200" : "border-[#6595FF] drop-shadow-[0px_8px_9.7px_rgba(26,86,219,0.2)]"}`}
            style={!car.isSoldOut ? { backgroundImage: PURPLE_GRADIENT } : undefined}
          >
            {car.isSoldOut ? "Sold Out" : "Book Now"}
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export default EvDailyCardMob;
